import {
    useContext,
    useMemo,
    useState,
    type FocusEvent,
    type HTMLAttributes,
    type KeyboardEvent,
    type MouseEvent,
    type ReactNode,
    type Ref,
} from "react";
import classNames from "classnames";
import { mergeRefs } from "@design-system/overlay/mergeRefs";
import { composeHandlers } from "@design-system/overlay/composeHandlers";
import {
    hasTextFieldFocus,
    isFromTextField,
} from "@design-system/overlay/textFields";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    BOARD_CARD_PROPS,
    BOARD_CARD_SURFACE_PROPS,
    hasFocusLeft,
} from "./boardAttributes";
import { BoardCardDragContext } from "./boardCardDrag";
import { BoardCardFocusContext } from "./boardCardFocus";
import { BoardCardDrawer } from "./BoardCardDrawer";
import { useBoardCardRoving } from "./boardRoving";
import {
    isFromCardControl,
    isFromInteractiveDescendant,
    isKeyboardFocusOnTabStop,
} from "./boardSensors";
import { useBoardCardSelection } from "./useBoardSelection";
import styles from "./BoardCard.module.scss";

export type BoardCardTone = "default" | "attention";

/**
 * What a wrapping trigger such as `Menu.Context` merges onto the card (its
 * handlers, style and state attributes); they reach the card's element.
 * Where the card's drag or selection sets the same handler, the trigger's
 * runs first, then the card's.
 */
type BoardCardTriggerProps = Omit<
    HTMLAttributes<HTMLDivElement>,
    | "children"
    | "className"
    | "role"
    | "tabIndex"
    | "onClick"
    | "onFocus"
    | "onMouseDown"
    | "onMouseDownCapture"
    | "onPointerDownCapture"
    | "onKeyDownCapture"
>;

interface BoardCardProps extends BoardCardTriggerProps {
    children: ReactNode;
    /**
     * A drawer under the card, part of the card (not the side panel
     * `Drawer`), usually a `BoardCardActivity`. Empty content (`null`,
     * `undefined`, `false`, `""`) is closed.
     */
    drawer?: ReactNode;
    tone?: BoardCardTone;
    /**
     * The card whose details are open beside the board: tinted like a
     * selected card, without being selected.
     */
    isActive?: boolean;
    /**
     * Makes the whole card a button that opens on click or Enter. Board
     * cards are focusable either way, for arrow-key navigation.
     */
    onClick?: (
        event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>,
    ) => void;
    /** Defaults to the card's text content. */
    "aria-label"?: string;
    /** Read after the board's own instructions. */
    "aria-describedby"?: string;
    className?: string;
    ref?: Ref<HTMLDivElement>;
}

const STATIC_OPEN_KEYS = new Set(["Enter", " "]);
// Space belongs to the drag sensor on a draggable board.
const DRAGGABLE_OPEN_KEYS = new Set(["Enter"]);

/**
 * Card shell for `Board`: a fixed 340px card with a hairline border that
 * lights on hover and focus, and compact rows. Fill it with
 * `BoardCardLayout`, or compose the rows with `BoardCardHeader`,
 * `BoardCardTitle`, `BoardCardMeta` and `BoardCardFooter`. On a draggable board the whole card is the drag
 * handle: click or Enter opens it, Space picks it up. A locked
 * card only opens, and shows its lock reason in a tooltip. On a board every
 * card is focusable for arrow-key navigation; without `onClick` Enter does
 * nothing. On a selectable board ⌘/Ctrl- and Shift-click select instead of
 * opening, a plain click or Enter also clears the selection, and a selected card
 * shows it (over `tone`).
 *
 * The card is one button-like control and the board's one Tab stop, so
 * controls inside it (inline-edit chips) are pointer targets with
 * `tabIndex={-1}`, or reached by Tab from their own focused card with
 * `useBoardCardTabStop`. A click on one, or in a popup it opens, neither opens the
 * card nor starts a drag (a `BoardCardTitleButton` still drags on press and
 * move); a ⌘/Ctrl- or Shift-click on one selects the card
 * instead of reaching the control; focus landing on one moves to the card,
 * unless the keyboard reached a control marked `BOARD_TAB_STOP_ATTRIBUTE`.
 * A text field in the card (an inline rename) is the exception: it keeps
 * focus and its own clicks, ⌘- and Shift-clicks included, even one ending
 * a text selection outside it, and the title holding it isn't clamped. The
 * card's keys never fire from it either way.
 *
 * `drawer` slides out from behind the card's bottom edge and moves with it.
 * Tone and selection stay on the card. Clicking the drawer opens the card
 * unless it holds a control (an expandable `BoardCardActivity`), pressing and
 * moving on it drags the card, and its text joins the card's name.
 */
export const BoardCard = ({
    children,
    drawer,
    tone = "default",
    isActive = false,
    onClick,
    "aria-label": ariaLabel,
    "aria-describedby": ariaDescribedBy,
    className,
    ref,
    onKeyDown,
    onPointerDown,
    onClickCapture,
    onBlur,
    ...triggerProps
}: Readonly<BoardCardProps>) => {
    const [isFocusWithin, setIsFocusWithin] = useState(false);
    const drag = useContext(BoardCardDragContext);
    const roving = useBoardCardRoving();
    const activatorRef = drag?.activatorRef;
    const { nodeRef } = roving;
    const cardRef = useMemo(
        () => mergeRefs([ref, activatorRef, nodeRef]),
        [ref, activatorRef, nodeRef],
    );
    const selection = useBoardCardSelection();
    const dragListeners = drag?.listeners;
    const isInteractive = onClick !== undefined;
    const isActionable = isInteractive || drag !== null;
    const tabIndex = roving.tabIndex ?? (isActionable ? 0 : undefined);
    const isFocusable = tabIndex !== undefined;
    // A board card that only takes focus for navigation is a named group.
    const plainRole = isFocusable ? "group" : undefined;
    const isDraggable = drag !== null && drag.lock === null;
    const openKeys = drag === null ? STATIC_OPEN_KEYS : DRAGGABLE_OPEN_KEYS;
    const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
        if (event.target !== event.currentTarget || drag?.isDragging) {
            return;
        }
        if (drag?.lock && event.key === " ") {
            event.preventDefault();
            drag.onLockedPickUp();
        } else if (onClick && openKeys.has(event.key)) {
            event.preventDefault();
            selection?.clear();
            onClick(event);
        }
    };
    const handleClick = (event: MouseEvent<HTMLElement>) => {
        if (
            isFromCardControl(event) ||
            hasTextFieldFocus(event.currentTarget) ||
            selection?.selectFromClick(event)
        ) {
            return;
        }
        selection?.clear();
        onClick?.(event);
    };
    // Controls inside the card are pointer only: focus handed back to one,
    // say by a picker as it closes, moves on to the card, so arrows and the
    // board's keys work again right away. Not while it's pressed: the press
    // is opening its popup, which closes when focus leaves for the card. Nor
    // for a text field, which is being typed in, or a Tab stop the keyboard
    // reached.
    const handleFocus = (event: FocusEvent<HTMLElement>) => {
        setIsFocusWithin(true);
        if (
            isFocusable &&
            isFromInteractiveDescendant(event) &&
            !isFromTextField(event) &&
            !isKeyboardFocusOnTabStop(event) &&
            !(
                event.target instanceof Element &&
                event.target.matches(":active")
            )
        ) {
            event.currentTarget.focus({ preventScroll: true });
        }
    };
    const instructionsId = isInteractive
        ? roving.describedByIds?.clickable
        : roving.describedByIds?.plain;
    const describedByIds = [
        selection?.isSelected && selection.describedById,
        instructionsId,
        ariaDescribedBy,
    ].filter(Boolean);

    const card = (
        <div
            {...triggerProps}
            {...drag?.attributes}
            {...drag?.listeners}
            ref={cardRef}
            className={classNames(styles.card, className, {
                [styles.interactive]: isInteractive,
                [styles.draggable]: isDraggable,
                [styles.lifted]: drag?.isLifted,
            })}
            data-tone={tone}
            data-selected={selection?.isSelected}
            data-active={isActive || undefined}
            {...BOARD_CARD_PROPS}
            role={isActionable ? "button" : plainRole}
            tabIndex={tabIndex}
            aria-label={ariaLabel}
            aria-describedby={
                describedByIds.length > 0 ? describedByIds.join(" ") : undefined
            }
            onClick={handleClick}
            {...selection?.pressHandlers}
            onKeyDown={composeHandlers(onKeyDown, (event) => {
                dragListeners?.onKeyDown?.(event);
            })}
            onPointerDown={composeHandlers(onPointerDown, (event) => {
                dragListeners?.onPointerDown?.(event);
            })}
            onClickCapture={composeHandlers(
                onClickCapture,
                selection?.pressHandlers.onClickCapture,
            )}
            onKeyDownCapture={isFocusable ? handleKeyDown : undefined}
            onFocus={handleFocus}
            onBlur={composeHandlers(onBlur, (event) => {
                if (hasFocusLeft(event)) {
                    setIsFocusWithin(false);
                }
            })}
        >
            <BoardCardFocusContext.Provider value={isFocusWithin}>
                <div className={styles.surface} {...BOARD_CARD_SURFACE_PROPS}>
                    {children}
                </div>
                <BoardCardDrawer>{drawer}</BoardCardDrawer>
            </BoardCardFocusContext.Provider>
        </div>
    );

    // Always wrapped on a draggable board: the tooltip turns itself off
    // without a reason, so a lock coming or going keeps the card (and focus).
    return drag === null ? (
        card
    ) : (
        <Tooltip content={drag.lock?.reason}>{card}</Tooltip>
    );
};
